/**
 * useScoreFeed - Hook for handling real-time score update notifications
 */
import { useEffect, useCallback, useRef } from 'react'
import { showToast } from '../components/ui/Toast'
import type { ScoreUpdate } from '../lib/websocket'

export function useScoreFeed() {
  const handledScoresRef = useRef<Set<string>>(new Set())

  const handleScoreUpdate = useCallback((data: ScoreUpdate) => {
    // Create a unique key for this update to prevent duplicates
    const updateKey = `${data.ticker}-${data.timestamp}`
    if (handledScoresRef.current.has(updateKey)) {
      return
    }
    handledScoresRef.current.add(updateKey)

    // Clean up old keys (keep last 100)
    if (handledScoresRef.current.size > 100) {
      const keys = Array.from(handledScoresRef.current)
      keys.slice(0, 50).forEach(k => handledScoresRef.current.delete(k))
    }

    const direction = data.direction === 'up' ? 'increased' : 'decreased'
    const severity = Math.abs(data.delta) >= 10 ? 'high' : 'medium'

    showToast({
      severity,
      title: `Score ${direction.charAt(0).toUpperCase() + direction.slice(1)}`,
      body: `${data.ticker} Cipher Score ${direction} from ${data.old_score.toFixed(0)} to ${data.new_score.toFixed(0)} (${data.delta > 0 ? '+' : ''}${data.delta.toFixed(1)})`,
      ticker: data.ticker,
      duration: 6000,
      action: {
        label: 'View',
        onClick: () => {
          window.location.href = `/watchlist?ticker=${data.ticker}`
        }
      }
    })
  }, [])

  useEffect(() => {
    // Listen for score update events from the price feed
    const handler = (event: CustomEvent<ScoreUpdate>) => {
      handleScoreUpdate(event.detail)
    }

    window.addEventListener('apex:score_update' as any, handler)
    return () => window.removeEventListener('apex:score_update' as any, handler)
  }, [handleScoreUpdate])
}

// Utility to dispatch score update events
export function dispatchScoreEvent(score: ScoreUpdate) {
  window.dispatchEvent(new CustomEvent('apex:score_update', { detail: score }))
}

export default useScoreFeed
